export const TRUE_MEANS_FAIL = new Set([]);

export const IGNORED_FOR_VERDICT = new Set([
  "DUPLICATE META KEYS",
  "NAME CONTAINS HTML",
]);

export const QA_METADATA_KEYS = new Set([
  "date_created",
  "ID",
  "slug",
  "permalink",
  "AI",
  "AI_FIXES",
  "failedTests",
  "verdict",
  "SKIPPED_TESTS",
  "SLUG LENGTH OK",
  "DATE VALIDE",
]);

export const OPENAI_HTML_REPAIR_TEST = "OPENAI HTML SYNTAX VALID";

export const LIA_ATTRIBUTE_REPAIR_TESTS = new Set([
  "CONDITION MATCH",
  "COLOR MATCH",
  "INSOTIT PRESENT",
  "TAX STATUS & CLASS",
]);

export const LIA_DESCRIPTION_REPAIR_TESTS = new Set([
  "MARIME INCALTAMINTE",
  "HAS PREOWNED SENTENCE",
  "PREOWNED IN CARACTERISTICI",
  "PREOWNED IN DESCRIERE",
]);

export const LIA_SEO_META_REPAIR_TESTS = new Set([
  "SEO TITLE CLEAN (NO HTML)",
  "SEO TITLE LENGTH OK",
  "SEO META DESC LENGTH OK",
  "OG TITLE MATCHES CLEAN NAME",
  "META DATA VALID",
]);

export const LIA_IMAGE_REPAIR_TESTS = new Set([
  "BRAND IN ALT",
  "MODEL IN ALT",
  "PRIMARY IMAGE ALT FORMAT OK",
  "MARIME IN ALT IMAGINI",
  "ALL IMAGES HAVE ALT",
  "BAD ALT IMAGES",
]);

export const LIA_SLUG_REPAIR_TESTS = new Set(["SLUG CLEAN"]);

export const LIA_DIRECT_REPAIR_TESTS = new Set([
  ...LIA_ATTRIBUTE_REPAIR_TESTS,
  ...LIA_DESCRIPTION_REPAIR_TESTS,
  ...LIA_SEO_META_REPAIR_TESTS,
  ...LIA_IMAGE_REPAIR_TESTS,
  ...LIA_SLUG_REPAIR_TESTS,
]);

const getTestKeyVariants = (testName) => {
  const key = String(testName || "");
  return Array.from(
    new Set([key, key.replace(/ /g, "_"), key.replace(/_/g, " ")]),
  );
};

export const shouldSkipSizeTestForSku = (testName, sku) => {
  const skuUpper = String(sku || "").toUpperCase();
  const isShoeSku = skuUpper.startsWith("SSLEB");
  return (
    (testName === "MARIME INCALTAMINTE" && !isShoeSku) ||
    (testName === "DIMENSIUNI SPECIFICE" && isShoeSku)
  );
};

export const shouldCountEntry = (testName, value) => {
  if (QA_METADATA_KEYS.has(testName)) return false;
  if (IGNORED_FOR_VERDICT.has(testName)) return false;
  if (value && typeof value === "object" && value.skipped === true)
    return false;
  if (typeof value === "string") return value.startsWith("ERROR:");
  return true;
};

export const isEntryFailed = (testName, value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.startsWith("ERROR:");
  if (value && typeof value === "object" && "passed" in value) {
    return value.passed === false;
  }
  return TRUE_MEANS_FAIL.has(testName) ? value === true : value === false;
};

export const getFailedQaTestNames = (tests = {}, sku = "") => {
  const skippedTests = Array.isArray(tests.SKIPPED_TESTS)
    ? tests.SKIPPED_TESTS
    : [];

  return Object.entries(tests)
    .filter(([testName, value]) => {
      if (!shouldCountEntry(testName, value)) return false;
      if (shouldSkipSizeTestForSku(testName, sku)) return false;
      if (
        getTestKeyVariants(testName).some((variant) =>
          skippedTests.includes(variant),
        )
      ) {
        return false;
      }
      return isEntryFailed(testName, value);
    })
    .map(([testName]) => testName);
};

export const getSkuStatus = (tests = {}, sku = "") =>
  getFailedQaTestNames(tests, sku).length > 0 ? "FAILED" : "PASSED";

export const getFailedSkuEntries = (data = {}) =>
  Object.entries(data).filter(
    ([sku, tests]) => getSkuStatus(tests || {}, sku) === "FAILED",
  );

export const buildLiaRepairPlan = (failedTestNames = []) => {
  const failed = Array.from(
    new Set(
      (failedTestNames || []).map((name) => String(name)).filter(Boolean),
    ),
  );
  const directDescription = failed.filter((name) =>
    LIA_DESCRIPTION_REPAIR_TESTS.has(name),
  );
  const directGroups = [
    {
      key: "attributes",
      label: "Atribute produs",
      failedKeys: failed.filter((name) => LIA_ATTRIBUTE_REPAIR_TESTS.has(name)),
    },
    {
      key: "seo-meta",
      label: "SEO și meta",
      failedKeys: failed.filter((name) => LIA_SEO_META_REPAIR_TESTS.has(name)),
    },
    {
      key: "images",
      label: "ALT imagini",
      failedKeys: failed.filter((name) => LIA_IMAGE_REPAIR_TESTS.has(name)),
    },
    {
      key: "slug",
      label: "Slug",
      failedKeys: failed.filter((name) => LIA_SLUG_REPAIR_TESTS.has(name)),
    },
    {
      key: "description",
      label: "Descriere produs",
      failedKeys: directDescription,
    },
  ].filter((group) => group.failedKeys.length > 0);
  const directNonDescription = directGroups
    .filter((group) => group.key !== "description")
    .flatMap((group) => group.failedKeys);
  const repairHtml = failed.includes(OPENAI_HTML_REPAIR_TEST);
  const manual = failed.filter(
    (name) =>
      !LIA_DIRECT_REPAIR_TESTS.has(name) && name !== OPENAI_HTML_REPAIR_TEST,
  );

  return {
    failed,
    directGroups,
    directNonDescription,
    directDescription,
    repairHtml,
    manual,
  };
};

export const extractQaRetestForSku = (qaRetest, sku) => {
  if (!qaRetest || typeof qaRetest !== "object" || Array.isArray(qaRetest)) {
    return null;
  }
  if (
    qaRetest[sku] &&
    typeof qaRetest[sku] === "object" &&
    !Array.isArray(qaRetest[sku])
  ) {
    return qaRetest[sku];
  }

  const fallbackCandidates = Object.entries(qaRetest).filter(
    ([key, value]) =>
      !["ok", "error", "details"].includes(key) &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value),
  );
  return fallbackCandidates.length === 1 ? fallbackCandidates[0][1] : null;
};
